import { getPresignedUrl } from "@/api/presign";

const CACHE_DURATION = 55 * 60 * 1000;
const urlCache = new Map();
const pendingRequests = new Map();

const CLOUDFLARE_PATTERN = /^https?:\/\/[^\/]+\.r2\.cloudflarestorage\.com\//;

function extractSrcPath(url) {
  try {
    const urlObj = new URL(url);
    return urlObj.pathname.slice(1);
  } catch {
    return null;
  }
}

function getCachedUrl(srcPath) {
  const cached = urlCache.get(srcPath);
  if (!cached) return null;
  
  if (Date.now() > cached.expireAt) {
    urlCache.delete(srcPath);
    return null;
  }
  
  return cached.signedUrl;
}

function setCachedUrl(srcPath, signedUrl) {
  urlCache.set(srcPath, {
    signedUrl,
    expireAt: Date.now() + CACHE_DURATION,
  });
}

async function fetchPresignedUrl(srcPath) {
  if (pendingRequests.has(srcPath)) {
    return pendingRequests.get(srcPath);
  }

  const promise = getPresignedUrl(srcPath)
    .then((signedUrl) => {
      setCachedUrl(srcPath, signedUrl);
      return signedUrl;
    })
    .finally(() => {
      pendingRequests.delete(srcPath);
    });

  pendingRequests.set(srcPath, promise);
  return promise;
}

async function getOrFetchPresignedUrl(srcPath) {
  const cached = getCachedUrl(srcPath);
  if (cached) return cached;
  
  return fetchPresignedUrl(srcPath);
}

function isCloudflareUrl(url) {
  if (typeof url === "string") {
    return CLOUDFLARE_PATTERN.test(url);
  }
  if (url instanceof URL) {
    return CLOUDFLARE_PATTERN.test(url.href);
  }
  if (url instanceof Request) {
    return CLOUDFLARE_PATTERN.test(url.url);
  }
  return false;
}

async function interceptFetch(input, init) {
  const urlString = input instanceof Request ? input.url : input instanceof URL ? input.href : input;
  
  if (isCloudflareUrl(urlString)) {
    const srcPath = extractSrcPath(urlString);
    if (srcPath) {
      try {
        const signedUrl = await getOrFetchPresignedUrl(srcPath);
        if (input instanceof Request) {
          return new Request(signedUrl, input);
        }
        return signedUrl;
      } catch (error) {
        console.error("[CloudflareInterceptor] Failed to get presigned URL:", error);
      }
    }
  }
  
  return input;
}

let originalFetch = null;
let originalOpen = null;
let originalSend = null;
const xhrUrlMap = new WeakMap();
let isInitialized = false;

function interceptedFetch(input, init) {
  return interceptFetch(input, init).then((processedInput) => {
    return originalFetch.call(window, processedInput, init);
  });
}

function interceptedOpen(method, url, ...rest) {
  if (typeof url === "string" && isCloudflareUrl(url)) {
    xhrUrlMap.set(this, { url, method });
  }
  return originalOpen.call(this, method, url, ...rest);
}

function interceptedSend(body) {
  const xhrInfo = xhrUrlMap.get(this);
  
  if (xhrInfo) {
    const srcPath = extractSrcPath(xhrInfo.url);
    if (srcPath) {
      getOrFetchPresignedUrl(srcPath)
        .then((signedUrl) => {
          originalOpen.call(this, xhrInfo.method, signedUrl, true);
          originalSend.call(this, body);
        })
        .catch((error) => {
          console.error("[CloudflareInterceptor] Failed to get presigned URL for XHR:", error);
          originalSend.call(this, body);
        })
        .finally(() => {
          xhrUrlMap.delete(this);
        });
      return;
    }
  }
  
  return originalSend.call(this, body);
}

export function initCloudflareInterceptor() {
  if (isInitialized) {
    console.warn("[CloudflareInterceptor] Already initialized");
    return;
  }
  
  originalFetch = window.fetch;
  originalOpen = XMLHttpRequest.prototype.open;
  originalSend = XMLHttpRequest.prototype.send;
  
  window.fetch = interceptedFetch;
  XMLHttpRequest.prototype.open = interceptedOpen;
  XMLHttpRequest.prototype.send = interceptedSend;
  
  isInitialized = true;
  console.log("[CloudflareInterceptor] Initialized - intercepting fetch and XMLHttpRequest");
}

export function restoreOriginalMethods() {
  if (!isInitialized) return;
  
  if (originalFetch) {
    window.fetch = originalFetch;
  }
  if (originalOpen) {
    XMLHttpRequest.prototype.open = originalOpen;
  }
  if (originalSend) {
    XMLHttpRequest.prototype.send = originalSend;
  }
  
  isInitialized = false;
  console.log("[CloudflareInterceptor] Restored original methods");
}

export function clearCache() {
  urlCache.clear();
  pendingRequests.clear();
}